import * as THREE from 'three';
import { solarSystemData, getPlanetData } from './solarSystemData.js';

/**
 * TrailSystem - Manages planet trail visualization with multiple trail types
 * Captures and stores historical positions to create heliocentric trajectory visualization
 */
export class TrailSystem {
    constructor(maxPoints = 1000) {
        this.maxPoints = maxPoints;
        this.trails = new Map();
        this.scene = null;
        this.isVisible = true;
        this.initialized = false; // Track initialization state

        // Performance optimization settings
        this.updateInterval = 3; // Update trails every N frames (reduced for smoother trails)
        this.frameCounter = 0;

        // Trail length configuration
        this.trailLengthMode = 'fixed'; // 'fixed' or 'proportional'
        this.trailLengthMultiplier = 1.0; // Multiplier for trail length (0.1 to 3.0)
        this.baseTrailLength = maxPoints; // Base trail length for fixed mode

        // Trail type configuration
        this.currentTrailType = 'line'; // Tipo de estela por defecto (base simple y segura)
        this.trailTypes = {
            line: {
                name: 'Línea',
                description: 'Línea continua clásica',
                icon: '━',
            },
            comet: {
                name: 'Cometa',
                description: 'Estela que se desvanece gradualmente (más brillante al inicio)',
                icon: '☄️',
            },
            comet_inverted: {
                name: 'Cometa Invertido',
                description: 'Estela que se desvanece gradualmente (más brillante al final)',
                icon: '🌠',
            },
        };

        // Staggered trail initialization system
        this.staggeredInit = {
            enabled: false, // Disabled by default - all trails appear immediately
            startTime: Date.now(),
            delayBetweenPlanets: 2000, // 2 seconds between each planet trail activation
            planetOrder: [], // Will be populated with planets sorted by size
            activatedPlanets: new Set(),
        };

        console.log(`TrailSystem initialized with max ${maxPoints} points per trail`);
    }

    /**
     * Calculate activation time for a planet based on its size order
     * @param {string} planetId - Planet identifier
     * @returns {number} Activation time in milliseconds from start
     */
    calculateActivationTime(planetId) {
        if (!this.staggeredInit.enabled) return 0;

        const index = this.staggeredInit.planetOrder.indexOf(planetId);
        if (index === -1) return 0;

        return index * this.staggeredInit.delayBetweenPlanets;
    }

    /**
     * Update staggered trail activation based on elapsed time
     */
    updateStaggeredActivation() {
        if (!this.staggeredInit.enabled) return;

        const currentTime = Date.now();
        const elapsedTime = currentTime - this.staggeredInit.startTime;

        this.staggeredInit.planetOrder.forEach((planetId) => {
            if (this.staggeredInit.activatedPlanets.has(planetId)) return;

            const trail = this.trails.get(planetId);
            if (!trail) return;

            if (elapsedTime >= trail.staggeredActivationTime) {
                trail.isActive = true;
                this.staggeredInit.activatedPlanets.add(planetId);
                console.log(
                    `Trail activated for ${trail.planetName} (${planetId}) after ${elapsedTime}ms`
                );
            }
        });
    }

    /**
     * Initialize the trail system with a Three.js scene
     * @param {THREE.Scene} scene - Three.js scene to add trails to
     */
    initializeWithScene(scene) {
        this.scene = scene;
        this.createTrailsForAllPlanets();
    }

    /**
     * Initialize trails with current planet positions to make them visible immediately
     * @param {Object} planetPositions - Current positions of all planets
     */
    initializeTrailPositions(planetPositions) {
        if (!planetPositions) {
            console.warn('No planet positions provided for trail initialization');
            return;
        }

        let initializedCount = 0;

        console.log('🔄 INITIALIZING TRAILS IN ORIGINAL ORDER (problem is planet-specific):');

        // Initialize each planet trail in original order
        solarSystemData.planets.forEach((planetData, index) => {
            const planetId = planetData.id;
            const trail = this.trails.get(planetId);
            const position = planetPositions[planetId];
            const originalPosition = solarSystemData.planets.indexOf(planetData) + 1;

            if (trail && position && trail.isActive) {
                // Clear any existing positions first
                trail.positions = [];

                // Add initial position multiple times to make trail visible immediately
                const initialPos = new THREE.Vector3(position.x, position.y, position.z);

                // Add more initial points to ensure visibility
                const initialPoints = Math.min(25, trail.maxPoints || 25);
                for (let i = 0; i < initialPoints; i++) {
                    trail.positions.push(initialPos.clone());
                }

                // Force immediate geometry update
                this.updateTrailGeometry(planetId);

                // Ensure the trail is visible in the scene
                if (trail.line) {
                    trail.line.visible = this.isVisible;
                }

                initializedCount++;

                // Log planets with focus on failing ones
                const failingPlanets = ['mercury', 'earth', 'jupiter', 'uranus'];
                const isFailing = failingPlanets.includes(planetId);

                console.log(
                    `${isFailing ? '❌ FAILING' : '✅ WORKING'} Planet ${index + 1} (${planetId}): initialized with ${initialPoints} points`
                );
            } else {
                // Log failures
                const failingPlanets = ['mercury', 'earth', 'jupiter', 'uranus'];
                const isFailing = failingPlanets.includes(planetId);

                console.error(
                    `❌ ${isFailing ? 'EXPECTED FAILING' : 'UNEXPECTED FAILING'} Planet ${index + 1} (${planetId}): FAILED - trail:${!!trail}, position:${!!position}, active:${trail?.isActive}`
                );
            }
        });

        console.log(
            `Trail initialization: ${initializedCount}/${this.trails.size} trails initialized`
        );

        // Force update all geometries to ensure they're rendered
        this.updateAllTrailGeometries();

        // Verify trails are actually visible after a short delay
        setTimeout(() => {
            this.verifyTrailVisibility();
            this.analyzePlanetProperties();
        }, 500);
    }

    /**
     * Create trail objects for all planets with unique colors
     */
    createTrailsForAllPlanets() {
        if (!this.scene) {
            console.warn('Scene not initialized. Call initializeWithScene() first.');
            return;
        }

        // Sort planets by size (radius) for staggered initialization
        this.staggeredInit.planetOrder = [...solarSystemData.planets]
            .sort((a, b) => a.radius - b.radius) // Smallest to largest
            .map((planet) => planet.id);

        console.log('🔄 CONFIRMED: Problem is PLANET-SPECIFIC, not position-based');
        console.log('Failing planets: mercury, earth, jupiter, uranus');

        // Create trails for each planet in original order
        solarSystemData.planets.forEach((planetData, index) => {
            this.createTrailForPlanet(planetData);

            // Log creation for failing planets specifically
            const failingPlanets = ['mercury', 'earth', 'jupiter', 'uranus'];
            if (failingPlanets.includes(planetData.id)) {
                console.log(`❌ Created trail for FAILING planet ${index + 1}: ${planetData.id}`);
            } else {
                console.log(`✅ Created trail for WORKING planet ${index + 1}: ${planetData.id}`);
            }
        });

        // Update trail lengths after all trails are created (but don't recreate geometry yet)
        this.updateAllTrailLengths();

        // Reset staggered initialization
        this.staggeredInit.startTime = Date.now();
        this.staggeredInit.activatedPlanets.clear();

        console.log(`Created trails for ${this.trails.size} planets in TEST ORDER`);
    }

    /**
     * Create a trail for a specific planet
     * @param {Object} planetData - Planet data from solarSystemData
     */
    createTrailForPlanet(planetData) {
        const trailData = {
            planetId: planetData.id,
            planetName: planetData.name,
            positions: [],
            geometry: null,
            material: null,
            line: null,
            particles: null, // For particle-based trails
            color: planetData.trailColor || planetData.color,
            isActive: !this.staggeredInit.enabled, // Start active unless staggered initialization is enabled
            trailType: this.currentTrailType,
            maxPoints: this.maxPoints, // Initialize with default, will be updated later
            staggeredActivationTime: this.calculateActivationTime(planetData.id),
            radius: planetData.radius, // Store radius for sorting
            frameCounter: 0, // Individual frame counter for each trail
        };

        this.createTrailGeometry(trailData);

        // Store trail data
        this.trails.set(planetData.id, trailData);
    }

    /**
     * Create geometry and material for a trail based on its type
     * @param {Object} trailData - Trail data object
     */
    createTrailGeometry(trailData) {
        // Remove existing trail objects from scene
        this.removeTrailFromScene(trailData);

        switch (trailData.trailType) {
            case 'line':
                this.createLineTrail(trailData);
                break;
            case 'comet':
                this.createCometTrail(trailData);
                break;
            case 'comet_inverted':
                this.createCometTrail(trailData, true); // Pass inverted flag
                break;
            default:
                this.createLineTrail(trailData);
        }

        // Add to scene
        if (trailData.line) {
            trailData.line.name = `trail_${trailData.planetId}`;
            trailData.line.visible = this.isVisible;
            this.scene.add(trailData.line);
        }
        if (trailData.particles) {
            trailData.particles.name = `trail_particles_${trailData.planetId}`;
            trailData.particles.visible = this.isVisible;
            this.scene.add(trailData.particles);
        }
    }

    /**
     * Create simple line trail
     * @param {Object} trailData - Trail data object
     */
    createLineTrail(trailData) {
        const maxPoints = trailData.maxPoints || this.maxPoints;
        trailData.geometry = new THREE.BufferGeometry();
        const positions = new Float32Array(maxPoints * 3);
        trailData.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        trailData.geometry.setDrawRange(0, 0);

        trailData.material = new THREE.LineBasicMaterial({
            color: trailData.color,
            transparent: true,
            opacity: 0.8,
            linewidth: 2,
        });

        trailData.line = new THREE.Line(trailData.geometry, trailData.material);
        // Desactivar frustum culling: el bounding sphere de un BufferGeometry dinámico (con
        // puntos sin usar en el origen y sin recalcular tras cada update) no coincide con lo
        // realmente dibujado, por lo que la estela desaparecía al ver la cámara desde el lado
        // o detrás. Las estelas están siempre cerca de los planetas en vista, así que no hace
        // falta cullearlas.
        trailData.line.frustumCulled = false;
    }

    /**
     * Create comet-like trail with gradient fade
     * @param {Object} trailData - Trail data object
     * @param {boolean} inverted - If true, trail is brighter at the end instead of beginning
     */
    createCometTrail(trailData, inverted = false) {
        // Store the inverted flag for later use in updates
        trailData.isInverted = inverted;
        const maxPoints = trailData.maxPoints || this.maxPoints;
        trailData.geometry = new THREE.BufferGeometry();
        const positions = new Float32Array(maxPoints * 3);
        const colors = new Float32Array(maxPoints * 3);

        trailData.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        trailData.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        trailData.geometry.setDrawRange(0, 0);

        trailData.material = new THREE.LineBasicMaterial({
            transparent: true,
            opacity: 0.8,
            linewidth: 3,
            vertexColors: true,
            blending: THREE.AdditiveBlending,
        });

        trailData.line = new THREE.Line(trailData.geometry, trailData.material);
        // Sin frustum culling (ver createLineTrail): evita que la estela desaparezca al orbitar.
        trailData.line.frustumCulled = false;
    }

    /**
     * Remove trail objects from scene
     * @param {Object} trailData - Trail data object
     */
    removeTrailFromScene(trailData) {
        if (trailData.line && this.scene) {
            this.scene.remove(trailData.line);
            if (trailData.geometry) trailData.geometry.dispose();
            if (trailData.material) trailData.material.dispose();
            trailData.line = null;
        }
        if (trailData.particles && this.scene) {
            this.scene.remove(trailData.particles);
            trailData.particles = null;
        }
    }

    /**
     * Add a new position point to a planet's trail
     * @param {string} planetId - Planet identifier
     * @param {THREE.Vector3|Object} position - Position to add (x, y, z)
     */
    addPoint(planetId, position) {
        // Update staggered activation before processing trails
        this.updateStaggeredActivation();

        const trail = this.trails.get(planetId);
        if (!trail || !trail.isActive || !position) return;

        // Convert position to Vector3 if needed
        const pos =
            position instanceof THREE.Vector3
                ? position
                : new THREE.Vector3(position.x, position.y, position.z);

        // Add position to history
        trail.positions.push(pos.clone());

        // Limit trail length using planet-specific length
        const maxPoints = trail.maxPoints || this.calculateTrailLength(planetId);

        // Ensure we always have a reasonable maximum
        const effectiveMaxPoints = Math.max(150, maxPoints);

        // Remove oldest points if we exceed the limit
        if (trail.positions.length > effectiveMaxPoints) {
            trail.positions.shift(); // Remove oldest point
        }

        // Update geometry more frequently for shorter trails to keep them smooth
        const trailMaxPoints = trail.maxPoints || this.maxPoints;
        const trailLength = trail.positions.length;

        // Calculate dynamic update interval based on trail characteristics
        let dynamicUpdateInterval = this.updateInterval;

        // For trails with very few points, update immediately to make them visible
        if (trailLength < 10) {
            dynamicUpdateInterval = 1;
        } else if (trailMaxPoints < 300) {
            // Very short trails need frequent updates
            dynamicUpdateInterval = 1;
        } else if (trailMaxPoints < 500) {
            // Medium trails need moderate updates
            dynamicUpdateInterval = Math.max(1, this.updateInterval - 1);
        }

        // Also consider current fill level
        const fillRatio = trailLength / trailMaxPoints;
        if (fillRatio < 0.5) {
            // Trail is still filling up, update more frequently
            dynamicUpdateInterval = Math.max(1, dynamicUpdateInterval - 1);
        }

        // Debug for problematic planets
        const failingPlanets = ['mercury', 'earth', 'jupiter', 'uranus'];
        const isProblematic = failingPlanets.includes(planetId);

        // Use individual frame counter for each trail
        trail.frameCounter++;
        const shouldUpdate = trail.frameCounter >= dynamicUpdateInterval || trailLength < 10;

        // if (isProblematic && shouldUpdate) {
        //     console.log(`🔄 Updating ${planetId} trail: positions=${trailLength}, frameCounter=${trail.frameCounter}, interval=${dynamicUpdateInterval}`);
        // }

        if (shouldUpdate) {
            this.updateTrailGeometry(planetId);
            trail.frameCounter = 0;
        }
    }

    /**
     * Update a planet's trail with a new position (alias for addPoint)
     * @param {string} planetId - Planet identifier
     * @param {THREE.Vector3|Object} position - New position
     */
    updateTrail(planetId, position) {
        this.addPoint(planetId, position);
    }

    /**
     * Update the geometry of a specific trail
     * @param {string} planetId - Planet identifier
     */
    updateTrailGeometry(planetId) {
        const trail = this.trails.get(planetId);
        if (!trail || !trail.geometry) {
            return;
        }

        // Use trail-specific maxPoints instead of global maxPoints
        const trailMaxPoints = trail.maxPoints || this.maxPoints;
        const pointCount = Math.min(trail.positions.length, trailMaxPoints);

        // Ensure we have points to draw
        if (pointCount === 0) {
            trail.geometry.setDrawRange(0, 0);
            return;
        }

        switch (trail.trailType) {
            case 'comet':
            case 'comet_inverted':
                this.updateCometTrail(trail, pointCount);
                break;
            case 'line':
            default:
                this.updateLineTrail(trail, pointCount);
        }

        // Double-check that drawRange was set correctly
        if (trail.geometry.drawRange.count !== pointCount) {
            console.warn(
                `Draw range mismatch for ${planetId}: expected ${pointCount}, got ${trail.geometry.drawRange.count}`
            );
            trail.geometry.setDrawRange(0, pointCount);
        }
    }

    /**
     * Update line-based trail geometry
     * @param {Object} trail - Trail data object
     * @param {number} pointCount - Number of points to draw
     */
    updateLineTrail(trail, pointCount) {
        const positions = trail.geometry.attributes.position.array;

        // Update position buffer
        for (let i = 0; i < pointCount; i++) {
            const pos = trail.positions[i];
            positions[i * 3] = pos.x;
            positions[i * 3 + 1] = pos.y;
            positions[i * 3 + 2] = pos.z;
        }

        // Update geometry
        trail.geometry.attributes.position.needsUpdate = true;
        trail.geometry.setDrawRange(0, pointCount);
    }

    /**
     * Update comet-style trail with gradient colors
     * @param {Object} trail - Trail data object
     * @param {number} pointCount - Number of points to draw
     */
    updateCometTrail(trail, pointCount) {
        const positions = trail.geometry.attributes.position.array;
        const colors = trail.geometry.attributes.color.array;

        const baseColor = new THREE.Color(trail.color);
        const isInverted = trail.isInverted || trail.trailType === 'comet_inverted';

        for (let i = 0; i < pointCount; i++) {
            const pos = trail.positions[i];
            let age = (pointCount - i) / pointCount; // Newer points have higher age value

            // Invert the age calculation for inverted comet trails
            if (isInverted) {
                age = 1 - age; // Older points become brighter
            }

            // Position
            positions[i * 3] = pos.x;
            positions[i * 3 + 1] = pos.y;
            positions[i * 3 + 2] = pos.z;

            // Color with gradient fade
            const intensity = age * age; // Quadratic fade for comet effect
            colors[i * 3] = baseColor.r * intensity;
            colors[i * 3 + 1] = baseColor.g * intensity;
            colors[i * 3 + 2] = baseColor.b * intensity;
        }

        // Update geometry
        trail.geometry.attributes.position.needsUpdate = true;
        trail.geometry.attributes.color.needsUpdate = true;
        trail.geometry.setDrawRange(0, pointCount);
    }

    /**
     * Force update all trail geometries
     */
    updateAllTrailGeometries() {
        // Update staggered activation first
        this.updateStaggeredActivation();

        this.trails.forEach((trail, planetId) => {
            this.updateTrailGeometry(planetId);
        });
    }

    /**
     * Clear all trails
     */
    clearTrails() {
        this.trails.forEach((trail) => {
            trail.positions = [];
            if (trail.geometry) {
                trail.geometry.setDrawRange(0, 0);
                trail.geometry.attributes.position.needsUpdate = true;
            }
        });
        console.log('All trails cleared');
    }

    /**
     * Clear a specific planet's trail
     * @param {string} planetId - Planet identifier
     */
    clearTrail(planetId) {
        const trail = this.trails.get(planetId);
        if (trail) {
            trail.positions = [];
            if (trail.geometry) {
                trail.geometry.setDrawRange(0, 0);
                trail.geometry.attributes.position.needsUpdate = true;
            }
            console.log(`Trail cleared for ${planetId}`);
        }
    }

    /**
     * Set the trail type for all trails
     * @param {string} trailType - Type of trail to use
     */
    setTrailType(trailType) {
        if (!this.trailTypes[trailType]) {
            console.warn(`Unknown trail type: ${trailType}`);
            return;
        }

        this.currentTrailType = trailType;

        // Recreate all trails with new type
        this.trails.forEach((trail, planetId) => {
            trail.trailType = trailType;
            this.createTrailGeometry(trail);
            // Preserve existing positions
            if (trail.positions.length > 0) {
                this.updateTrailGeometry(planetId);
            }
        });

        console.log(`Trail type changed to: ${this.trailTypes[trailType].name}`);
    }

    /**
     * Get current trail type
     * @returns {string} Current trail type
     */
    getTrailType() {
        return this.currentTrailType;
    }

    /**
     * Get available trail types
     * @returns {Object} Available trail types with their configurations
     */
    getAvailableTrailTypes() {
        return { ...this.trailTypes };
    }

    /**
     * Set trail type for a specific planet
     * @param {string} planetId - Planet identifier
     * @param {string} trailType - Type of trail to use
     */
    setPlanetTrailType(planetId, trailType) {
        const trail = this.trails.get(planetId);
        if (!trail) {
            console.warn(`Trail not found for planet: ${planetId}`);
            return;
        }

        if (!this.trailTypes[trailType]) {
            console.warn(`Unknown trail type: ${trailType}`);
            return;
        }

        trail.trailType = trailType;
        this.createTrailGeometry(trail);

        // Preserve existing positions
        if (trail.positions.length > 0) {
            this.updateTrailGeometry(planetId);
        }

        console.log(`Trail type for ${planetId} changed to: ${this.trailTypes[trailType].name}`);
    }

    /**
     * Set visibility of all trails
     * @param {boolean} visible - Whether trails should be visible
     */
    setVisible(visible) {
        this.isVisible = visible;
        this.trails.forEach((trail) => {
            if (trail.line) {
                trail.line.visible = visible;
            }
            if (trail.particles) {
                trail.particles.visible = visible;
            }
        });
        console.log(`Trails visibility set to: ${visible}`);
    }

    /**
     * Set visibility of a specific trail
     * @param {string} planetId - Planet identifier
     * @param {boolean} visible - Whether trail should be visible
     */
    setTrailVisible(planetId, visible) {
        const trail = this.trails.get(planetId);
        if (trail && trail.line) {
            trail.line.visible = visible;
        }
    }

    /**
     * Set maximum trail length (number of points)
     * @param {number} length - Maximum number of points per trail
     */
    setTrailLength(length) {
        const oldMaxPoints = this.maxPoints;
        this.maxPoints = Math.max(10, Math.min(length, 5000)); // Clamp between 10 and 5000

        if (this.maxPoints !== oldMaxPoints) {
            // Recreate geometries with new buffer size
            this.trails.forEach((trail, planetId) => {
                // Recortar posiciones si exceden el nuevo máximo (antes lo hacía el método duplicado)
                if (trail.positions.length > this.maxPoints) {
                    trail.positions = trail.positions.slice(-this.maxPoints);
                }
                this.recreateTrailGeometry(planetId, this.maxPoints);
            });
            console.log(`Trail length updated to ${this.maxPoints} points`);
        }
    }

    /**
     * Set color for a specific trail
     * @param {string} planetId - Planet identifier
     * @param {number} color - Hex color value
     */
    setTrailColor(planetId, color) {
        const trail = this.trails.get(planetId);
        if (trail && trail.material) {
            trail.material.color.setHex(color);
            trail.color = color;
        }
    }

    /**
     * Set opacity for all trails
     * @param {number} opacity - Opacity value (0-1)
     */
    setTrailOpacity(opacity) {
        const clampedOpacity = Math.max(0, Math.min(1, opacity));
        this.trails.forEach((trail) => {
            if (trail.material) {
                trail.material.opacity = clampedOpacity;
            }
        });
    }

    /**
     * Get trail data for a specific planet
     * @param {string} planetId - Planet identifier
     * @returns {Object|null} Trail data or null if not found
     */
    getTrailData(planetId) {
        return this.trails.get(planetId) || null;
    }

    /**
     * Get statistics about the trail system
     * @returns {Object} Trail system statistics
     */
    getStatistics() {
        let totalPoints = 0;
        let activeTrails = 0;
        let visibleTrails = 0;

        this.trails.forEach((trail) => {
            totalPoints += trail.positions.length;
            if (trail.isActive) activeTrails++;
            if (trail.line && trail.line.visible) visibleTrails++;
        });

        return {
            totalTrails: this.trails.size,
            activeTrails,
            visibleTrails,
            totalPoints,
            maxPointsPerTrail: this.maxPoints,
            memoryUsage: totalPoints * 3 * 4, // Approximate bytes (3 floats per point)
        };
    }

    /**
     * Enable or disable a specific trail
     * @param {string} planetId - Planet identifier
     * @param {boolean} active - Whether trail should be active
     */
    setTrailActive(planetId, active) {
        const trail = this.trails.get(planetId);
        if (trail) {
            trail.isActive = active;
            if (!active) {
                this.clearTrail(planetId);
            }
        }
    }

    /**
     * Set trail length mode
     * @param {string} mode - 'fixed' or 'proportional'
     */
    setTrailLengthMode(mode) {
        if (mode !== 'fixed' && mode !== 'proportional') {
            console.warn(`Unknown trail length mode: ${mode}. Using 'fixed'.`);
            mode = 'fixed';
        }

        this.trailLengthMode = mode;
        this.updateAllTrailLengths();

        // Force immediate update of all trail geometries
        this.trails.forEach((trail, planetId) => {
            if (trail.positions.length > 0) {
                this.updateTrailGeometry(planetId);
            }
        });

        console.log(`Trail length mode set to: ${mode}`);
    }

    /**
     * Get current trail length mode
     * @returns {string} Current trail length mode
     */
    getTrailLengthMode() {
        return this.trailLengthMode;
    }

    /**
     * Set trail length multiplier
     * @param {number} multiplier - Multiplier value (0.1 to 3.0)
     */
    setTrailLengthMultiplier(multiplier) {
        this.trailLengthMultiplier = Math.max(0.1, Math.min(3.0, multiplier));
        this.updateAllTrailLengths();

        // Force immediate update of all trail geometries
        this.trails.forEach((trail, planetId) => {
            if (trail.positions.length > 0) {
                this.updateTrailGeometry(planetId);
            }
        });

        console.log(`Trail length multiplier set to: ${this.trailLengthMultiplier}`);
    }

    /**
     * Get current trail length multiplier
     * @returns {number} Current trail length multiplier
     */
    getTrailLengthMultiplier() {
        return this.trailLengthMultiplier;
    }

    /**
     * Calculate effective trail length for a planet
     * @param {string} planetId - Planet identifier
     * @returns {number} Effective trail length in points
     */
    calculateTrailLength(planetId) {
        let baseLength;

        if (this.trailLengthMode === 'proportional') {
            // Get planet data to calculate proportional length
            const planetData = getPlanetData(planetId);
            if (planetData) {
                // Use orbital period to determine trail length, but with better scaling
                const earthOrbitalPeriod = 365.256; // Earth's orbital period in days
                const planetPeriod = planetData.orbitalPeriod;

                // Use logarithmic scaling to prevent extreme values
                // This gives a more balanced distribution
                const periodRatio = planetPeriod / earthOrbitalPeriod;
                const logFactor = Math.log10(1 + periodRatio * 9) / Math.log10(10); // Normalize to 0-1 range

                // Scale between 60% and 140% of base length
                const scaleFactor = 0.6 + logFactor * 0.8;
                baseLength = Math.round(this.baseTrailLength * scaleFactor);

                // Ensure reasonable limits for all planets
                const minLength = Math.max(200, this.baseTrailLength * 0.3); // At least 30% of base, minimum 200
                const maxLength = this.baseTrailLength * 2.0; // At most 200% of base
                baseLength = Math.max(minLength, Math.min(maxLength, baseLength));

                console.log(
                    `Planet ${planetId}: period=${planetPeriod.toFixed(1)} days, ratio=${periodRatio.toFixed(2)}, scale=${scaleFactor.toFixed(2)}, length=${baseLength}`
                );
            } else {
                baseLength = this.baseTrailLength;
            }
        } else {
            // Fixed mode - same length for all planets
            baseLength = this.baseTrailLength;
        }

        // Apply multiplier and ensure minimum viable length
        const finalLength = Math.round(baseLength * this.trailLengthMultiplier);
        return Math.max(150, finalLength); // Ensure minimum of 150 points for smooth trails
    }

    /**
     * Update trail lengths for all planets based on current settings
     */
    updateAllTrailLengths() {
        this.trails.forEach((trail, planetId) => {
            const newLength = this.calculateTrailLength(planetId);
            const oldLength = trail.maxPoints || this.maxPoints;

            // Update maxPoints for this specific trail
            trail.maxPoints = newLength;

            // Only recreate geometry if length changed significantly AND we're not in initial setup
            if (Math.abs(newLength - oldLength) > 5 && this.initialized) {
                // Trim positions if needed (keep the most recent ones)
                if (trail.positions.length > newLength) {
                    trail.positions = trail.positions.slice(-newLength);
                }

                // Recreate geometry with new size
                this.recreateTrailGeometry(planetId, newLength);

                console.log(`Updated trail length for ${planetId}: ${oldLength} -> ${newLength}`);
            }
        });
    }

    /**
     * Recreate trail geometry with new point count
     * @param {string} planetId - Planet identifier
     * @param {number} newMaxPoints - New maximum points for this trail
     */
    recreateTrailGeometry(planetId, newMaxPoints) {
        const trail = this.trails.get(planetId);
        if (!trail) return;

        // Remove old geometry from scene
        if (trail.line && this.scene) {
            this.scene.remove(trail.line);
        }

        // Dispose old geometry
        if (trail.geometry) {
            trail.geometry.dispose();
        }

        // Create new geometry with new size
        trail.geometry = new THREE.BufferGeometry();
        const positions = new Float32Array(newMaxPoints * 3);
        trail.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

        // For comet trails, also recreate color attribute
        if (trail.trailType === 'comet' || trail.trailType === 'comet_inverted') {
            const colors = new Float32Array(newMaxPoints * 3);
            trail.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        }

        trail.geometry.setDrawRange(0, 0);

        // Create new line with existing material
        trail.line = new THREE.Line(trail.geometry, trail.material);
        // Sin frustum culling (ver createLineTrail): evita que la estela desaparezca al orbitar.
        trail.line.frustumCulled = false;
        trail.line.name = `trail_${planetId}`;
        trail.line.visible = this.isVisible;

        // Add back to scene
        if (this.scene) {
            this.scene.add(trail.line);
        }

        // Update geometry with current positions
        this.updateTrailGeometry(planetId);
    }

    /**
     * Enable or disable staggered trail initialization
     * @param {boolean} enabled - Whether to use staggered initialization
     */
    setStaggeredInitialization(enabled) {
        this.staggeredInit.enabled = enabled;

        if (!enabled) {
            // Activate all trails immediately
            this.trails.forEach((trail) => {
                trail.isActive = true;
            });
            this.staggeredInit.activatedPlanets.clear();
            solarSystemData.planets.forEach((planet) => {
                this.staggeredInit.activatedPlanets.add(planet.id);
            });
        } else {
            // Reset staggered initialization
            this.resetStaggeredInitialization();
        }

        console.log(`Staggered trail initialization ${enabled ? 'enabled' : 'disabled'}`);
    }

    /**
     * Set delay between planet trail activations
     * @param {number} delay - Delay in milliseconds
     */
    setStaggeredDelay(delay) {
        this.staggeredInit.delayBetweenPlanets = Math.max(500, delay);
        console.log(`Staggered delay set to ${this.staggeredInit.delayBetweenPlanets}ms`);
    }

    /**
     * Reset staggered initialization system
     */
    resetStaggeredInitialization() {
        this.staggeredInit.startTime = Date.now();
        this.staggeredInit.activatedPlanets.clear();

        // Deactivate all trails
        this.trails.forEach((trail) => {
            trail.isActive = false;
            // Clear existing positions to start fresh
            trail.positions = [];
            if (trail.geometry) {
                trail.geometry.setDrawRange(0, 0);
                trail.geometry.attributes.position.needsUpdate = true;
            }
        });

        console.log('Staggered trail initialization reset');
    }

    /**
     * Get staggered initialization status
     * @returns {Object} Status information
     */
    getStaggeredStatus() {
        const currentTime = Date.now();
        const elapsedTime = currentTime - this.staggeredInit.startTime;

        return {
            enabled: this.staggeredInit.enabled,
            elapsedTime,
            delayBetweenPlanets: this.staggeredInit.delayBetweenPlanets,
            planetOrder: [...this.staggeredInit.planetOrder],
            activatedPlanets: [...this.staggeredInit.activatedPlanets],
            totalPlanets: this.staggeredInit.planetOrder.length,
            activatedCount: this.staggeredInit.activatedPlanets.size,
        };
    }

    /**
     * Get trail length information for all planets
     * @returns {Object} Trail length information
     */
    getTrailLengthInfo() {
        const info = {
            mode: this.trailLengthMode,
            multiplier: this.trailLengthMultiplier,
            baseLength: this.baseTrailLength,
            planets: {},
        };

        this.trails.forEach((trail, planetId) => {
            const planetData = getPlanetData(planetId);
            info.planets[planetId] = {
                name: planetData?.name || planetId,
                orbitalPeriod: planetData?.orbitalPeriod || 0,
                effectiveLength: this.calculateTrailLength(planetId),
                currentPoints: trail.positions.length,
                maxPoints: trail.maxPoints || this.maxPoints,
                isActive: trail.isActive,
                hasGeometry: !!trail.geometry,
            };
        });

        return info;
    }

    /**
     * Print debug information about trail lengths (for development)
     */
    debugTrailLengths() {
        const info = this.getTrailLengthInfo();
        console.log(`\n=== Trail Length Debug Info ===`);
        console.log(`Mode: ${info.mode}`);
        console.log(`Multiplier: ${info.multiplier}x`);
        console.log(`Base Length: ${info.baseLength}`);
        console.log(`\nPlanet Details:`);

        Object.entries(info.planets).forEach(([planetId, data]) => {
            const status = data.isActive ? 'active' : 'inactive';
            const geometry = data.hasGeometry ? 'OK' : 'NO GEOM';
            console.log(
                `${data.name.padEnd(10)}: ${data.orbitalPeriod.toFixed(1).padStart(8)} days | ${data.effectiveLength.toString().padStart(4)} max | ${data.currentPoints.toString().padStart(3)} current | ${status} | ${geometry}`
            );
        });
        console.log(`===============================\n`);
    }

    /**
     * Debug method to log trail status
     */
    debugTrailStatus() {
        console.log('=== Trail System Debug ===');
        console.log(`Mode: ${this.trailLengthMode}, Multiplier: ${this.trailLengthMultiplier}`);

        this.trails.forEach((trail, planetId) => {
            const planetData = getPlanetData(planetId);
            console.log(`${planetId} (${planetData?.name}):`, {
                orbitalPeriod: planetData?.orbitalPeriod,
                maxPoints: trail.maxPoints,
                currentPoints: trail.positions.length,
                isActive: trail.isActive,
                hasLine: !!trail.line,
                hasGeometry: !!trail.geometry,
            });
        });
    }

    /**
     * Dispose of all trail resources
     */
    dispose() {
        this.trails.forEach((trail) => {
            // Remove from scene
            if (trail.line && this.scene) {
                this.scene.remove(trail.line);
            }
            if (trail.particles && this.scene) {
                this.scene.remove(trail.particles);
            }

            // Dispose geometry and material
            if (trail.geometry) {
                trail.geometry.dispose();
            }
            if (trail.material) {
                trail.material.dispose();
            }
        });

        this.trails.clear();
        this.scene = null;
        console.log('TrailSystem disposed');
    }

    /**
     * Get all planet IDs that have trails
     * @returns {Array<string>} Array of planet IDs
     */
    getPlanetIds() {
        return Array.from(this.trails.keys());
    }

    /**
     * Check if trails are currently visible
     * @returns {boolean} True if trails are visible
     */
    isTrailsVisible() {
        return this.isVisible;
    }

    /**
     * Get current maximum points per trail
     * @returns {number} Maximum points per trail
     */
    getMaxPoints() {
        return this.maxPoints;
    }

    /**
     * Verify trail states after initialization
     */
    verifyTrailStates() {
        console.log('=== TRAIL STATE VERIFICATION ===');

        solarSystemData.planets.forEach((planetData, index) => {
            const planetId = planetData.id;
            const trail = this.trails.get(planetId);

            if (trail) {
                const status = {
                    index,
                    planetId,
                    name: trail.planetName,
                    isActive: trail.isActive,
                    positions: trail.positions.length,
                    hasGeometry: !!trail.geometry,
                    hasLine: !!trail.line,
                    visible: trail.line ? trail.line.visible : false,
                    drawRange: trail.geometry ? trail.geometry.drawRange.count : 0,
                    inScene: trail.line ? this.scene.children.includes(trail.line) : false,
                };

                const isOdd = index % 2 === 0; // 0-based index, so 0,2,4,6 are odd positions (1,3,5,7)
                console.log(
                    `${isOdd ? '🔴 ODD' : '🟢 EVEN'} Planet ${index + 1}: ${planetId}`,
                    status
                );

                if (status.positions === 0) {
                    console.error(`❌ ${planetId} has NO POSITIONS!`);
                }
                if (!status.hasLine) {
                    console.error(`❌ ${planetId} has NO LINE!`);
                }
                if (!status.inScene) {
                    console.error(`❌ ${planetId} is NOT IN SCENE!`);
                }
            } else {
                console.error(`❌ No trail found for ${planetId}`);
            }
        });

        console.log('=== END VERIFICATION ===');
    }

    /**
     * Debug planet positions to check if failing planets have position issues
     * @param {Object} planetPositions - Current positions of all planets
     */
    debugPlanetPositions(planetPositions) {
        console.log('🔍 DEBUGGING PLANET POSITIONS:');

        const failingPlanets = ['mercury', 'earth', 'jupiter', 'uranus'];
        const workingPlanets = ['venus', 'mars', 'saturn', 'neptune'];

        console.log('❌ FAILING PLANETS POSITIONS:');
        failingPlanets.forEach((planetId) => {
            const position = planetPositions[planetId];
            console.log(`${planetId}:`, position);

            if (position) {
                const distance = Math.sqrt(
                    position.x * position.x + position.y * position.y + position.z * position.z
                );
                console.log(`  Distance from origin: ${distance.toFixed(2)}`);
                console.log(
                    `  Valid coordinates: x=${!isNaN(position.x)}, y=${!isNaN(position.y)}, z=${!isNaN(position.z)}`
                );
            } else {
                console.error(`  ❌ NO POSITION DATA for ${planetId}!`);
            }
        });

        console.log('✅ WORKING PLANETS POSITIONS:');
        workingPlanets.forEach((planetId) => {
            const position = planetPositions[planetId];
            console.log(`${planetId}:`, position);

            if (position) {
                const distance = Math.sqrt(
                    position.x * position.x + position.y * position.y + position.z * position.z
                );
                console.log(`  Distance from origin: ${distance.toFixed(2)}`);
            }
        });
    }

    /**
     * Force re-initialization of all trails with current planet positions
     * @param {Object} planetPositions - Current positions of all planets
     */
    forceReinitialize(planetPositions) {
        console.log('Forcing trail system re-initialization...');

        // Debug positions first
        this.debugPlanetPositions(planetPositions);

        // Use setTimeout to allow UI to update before heavy processing
        setTimeout(() => {
            // Clear all existing trails
            this.clearTrails();

            // Force all trails to be active
            this.trails.forEach((trail) => {
                trail.isActive = true;
            });

            // Simple direct initialization for each planet
            solarSystemData.planets.forEach((planetData, index) => {
                const planetId = planetData.id;
                const trail = this.trails.get(planetId);
                const position = planetPositions[planetId];

                if (trail && position) {
                    // Clear positions
                    trail.positions = [];

                    // Add initial points
                    const initialPos = new THREE.Vector3(position.x, position.y, position.z);
                    const initialPoints = 30; // Fixed number for consistency

                    for (let i = 0; i < initialPoints; i++) {
                        trail.positions.push(initialPos.clone());
                    }

                    // Update geometry
                    this.updateTrailGeometry(planetId);

                    // Ensure visibility
                    if (trail.line) {
                        trail.line.visible = this.isVisible;
                    }

                    const failingPlanets = ['mercury', 'earth', 'jupiter', 'uranus'];
                    if (failingPlanets.includes(planetId)) {
                        console.log(`🔴 Reinitialized FAILING planet: ${planetId}`);
                    }
                }
            });

            this.initialized = true;

            console.log('Trail system re-initialization complete');
        }, 100);
    }

    /**
     * Verify trail visibility after initialization
     */
    verifyTrailVisibility() {
        console.log('=== TRAIL VISIBILITY CHECK (TEST ORDER) ===');

        const testOrder = [
            solarSystemData.planets[1], // venus
            solarSystemData.planets[0], // mercury
            solarSystemData.planets[3], // mars
            solarSystemData.planets[2], // earth
            solarSystemData.planets[5], // saturn
            solarSystemData.planets[4], // jupiter
            solarSystemData.planets[7], // neptune
            solarSystemData.planets[6], // uranus
        ];

        testOrder.forEach((planetData, index) => {
            const planetId = planetData.id;
            const trail = this.trails.get(planetId);
            const isOdd = index % 2 === 0;
            const originalPosition = solarSystemData.planets.indexOf(planetData) + 1;
            const wasOriginallyOdd = (originalPosition - 1) % 2 === 0;

            if (trail) {
                const drawRange = trail.geometry ? trail.geometry.drawRange.count : 0;
                const inScene = trail.line ? this.scene.children.includes(trail.line) : false;

                console.log(
                    `${isOdd ? '🔴 ODD' : '🟢 EVEN'} Planet ${index + 1} (${planetId}) [Originally ${originalPosition}${wasOriginallyOdd ? ' - was ODD' : ' - was EVEN'}]:`
                );
                console.log(`  - Positions: ${trail.positions.length}`);
                console.log(`  - Draw range: ${drawRange}`);
                console.log(`  - In scene: ${inScene}`);
                console.log(`  - Line visible: ${trail.line ? trail.line.visible : false}`);

                if (drawRange === 0) {
                    console.error(`❌ ${planetId} has ZERO draw range!`);
                    this.fixTrailDrawRange(planetId);
                }
            }
        });

        console.log('=== END VISIBILITY CHECK ===');
        console.log(
            '🔍 ANALYSIS: Check if the SAME planets fail (planet-specific) or if NEW odd positions fail (position-based)'
        );
    }

    /**
     * Fix trail draw range if it's zero
     */
    fixTrailDrawRange(planetId) {
        const trail = this.trails.get(planetId);
        if (trail && trail.geometry && trail.positions.length > 0) {
            const pointCount = Math.min(trail.positions.length, trail.maxPoints || this.maxPoints);
            trail.geometry.setDrawRange(0, pointCount);
            trail.geometry.attributes.position.needsUpdate = true;

            if (trail.geometry.attributes.color) {
                trail.geometry.attributes.color.needsUpdate = true;
            }

            console.log(`🔧 Fixed draw range for ${planetId}: ${pointCount} points`);
        }
    }

    /**
     * Analyze failing planets to find common patterns
     */
    analyzePlanetProperties() {
        console.log('🔍 ANALYZING PLANET PROPERTIES:');

        const failingPlanets = ['mercury', 'earth', 'jupiter', 'uranus'];
        const workingPlanets = ['venus', 'mars', 'saturn', 'neptune'];

        console.log('❌ FAILING PLANETS:');
        failingPlanets.forEach((planetId) => {
            const planetData = getPlanetData(planetId);
            if (planetData) {
                console.log(`${planetId}:`, {
                    orbitalPeriod: planetData.orbitalPeriod,
                    rotationPeriod: planetData.rotationPeriod,
                    orbitalEccentricity: planetData.orbitalEccentricity,
                    orbitalInclination: planetData.orbitalInclination,
                    hasRings: planetData.hasRings,
                    axialTilt: planetData.axialTilt,
                    trailColor: planetData.trailColor?.toString(16),
                    color: planetData.color?.toString(16),
                });
            }
        });

        console.log('✅ WORKING PLANETS:');
        workingPlanets.forEach((planetId) => {
            const planetData = getPlanetData(planetId);
            if (planetData) {
                console.log(`${planetId}:`, {
                    orbitalPeriod: planetData.orbitalPeriod,
                    rotationPeriod: planetData.rotationPeriod,
                    orbitalEccentricity: planetData.orbitalEccentricity,
                    orbitalInclination: planetData.orbitalInclination,
                    hasRings: planetData.hasRings,
                    axialTilt: planetData.axialTilt,
                    trailColor: planetData.trailColor?.toString(16),
                    color: planetData.color?.toString(16),
                });
            }
        });
    }

    /**
     * Force immediate updates for problematic trails
     */
    forceUpdateProblematicTrails() {
        console.log('🔧 Force updating problematic trails...');

        const failingPlanets = ['mercury', 'earth', 'jupiter', 'uranus'];

        failingPlanets.forEach((planetId) => {
            const trail = this.trails.get(planetId);
            if (trail) {
                // Reset frame counter to force immediate update
                trail.frameCounter = 999;

                // Force geometry update
                this.updateTrailGeometry(planetId);

                console.log(
                    `🔧 Force updated ${planetId} trail: positions=${trail.positions.length}`
                );
            }
        });
    }

    /**
     * Force fix for specific failing planets
     */
    forceFixOddTrails() {
        console.log('🔧 Force fixing failing planet trails...');

        // First analyze the properties
        this.analyzePlanetProperties();

        // Force immediate updates
        this.forceUpdateProblematicTrails();

        const failingPlanets = ['mercury', 'earth', 'jupiter', 'uranus'];

        failingPlanets.forEach((planetId) => {
            const trail = this.trails.get(planetId);
            if (trail) {
                // Ensure trail is active
                trail.isActive = true;

                // Ensure trail has positions
                if (trail.positions.length === 0) {
                    // Add dummy positions if none exist
                    const dummyPos = new THREE.Vector3(0, 0, 0);
                    for (let i = 0; i < 30; i++) {
                        trail.positions.push(dummyPos.clone());
                    }
                }

                // Force geometry update
                this.updateTrailGeometry(planetId);

                // Ensure visibility
                if (trail.line) {
                    trail.line.visible = true;

                    // Force add to scene if not present
                    if (!this.scene.children.includes(trail.line)) {
                        this.scene.add(trail.line);
                        console.log(`🔧 Re-added ${planetId} trail to scene`);
                    }
                }

                console.log(`🔧 Force fixed trail for ${planetId}`);
            }
        });
    }

    /**
     * Diagnostic method to check trail state
     */
    diagnoseTrailState() {
        console.log('=== TRAIL SYSTEM DIAGNOSTIC ===');
        console.log(`Total trails: ${this.trails.size}`);
        console.log(`Staggered enabled: ${this.staggeredInit.enabled}`);
        console.log(`Initialized: ${this.initialized}`);

        this.trails.forEach((trail, planetId) => {
            const status = {
                planetId,
                name: trail.planetName,
                isActive: trail.isActive,
                positions: trail.positions.length,
                hasGeometry: !!trail.geometry,
                hasLine: !!trail.line,
                visible: trail.line ? trail.line.visible : false,
                drawRange: trail.geometry ? trail.geometry.drawRange.count : 0,
            };
            console.log(`${planetId}:`, status);
        });
        console.log('=== END DIAGNOSTIC ===');
    }
}
